import React, { useCallback } from "react";
import useQuantityModalStore from "../../store/quantityModalStore";
import { useShallow } from "zustand/shallow";
import { useOrderOverlay } from "../../hooks/useOrderOverlay";
import QuantityEditModal from "./QuantityEditModal";

/**
 * QuantityModalProvider - Renders the QuantityEditModal at the App level
 *
 * This component is mounted at the App level (outside ChartWrapper) to prevent
 * the tablet remount loop issue. ChartWrapper does NOT subscribe to the modal
 * store, so modal state changes don't cause chart re-renders.
 */
const QuantityModalProvider = () => {
  // Get modal state from Zustand store using shallow comparison
  const quantityModalVisible = useQuantityModalStore((state) => state.visible);
  const quantityModalData = useQuantityModalStore(
    useShallow((state) => ({
      orderId: state.orderId,
      currentQuantity: state.currentQuantity,
      orderType: state.orderType,
      price: state.price,
      symbol: state.symbol,
      order: state.order,
    })),
  );
  const closeQuantityModal = useQuantityModalStore((state) => state.closeModal);

  // Get order update function from useOrderOverlay - use the symbol from modal data
  const { updateOrderComplete } = useOrderOverlay(quantityModalData.symbol);

  const handleQuantityModalConfirm = useCallback(
    (newQuantity, newPrice, otoData = null) => {
      const {
        orderId,
        currentQuantity,
        price: currentPrice,
        order,
      } = quantityModalData;
      if (!orderId || !newQuantity || newQuantity <= 0) {
        closeQuantityModal();
        return;
      }

      const quantityChanged = newQuantity !== currentQuantity;
      const priceChanged = newPrice !== undefined && newPrice !== currentPrice;

      // Build updates object — always include price so buildReplaceOrder uses the
      // value the user confirmed in the modal, not a potentially stale
      // originalRouterOrder price (e.g. when the user dragged before opening the modal
      // and the replace report hasn't arrived yet).
      const updates = {};
      const otoUpdates = [];

      if (quantityChanged) {
        updates.quantity = newQuantity;
      }
      if (newPrice !== undefined) {
        updates.price = newPrice;
      }

      // Collect OTO price changes
      if (otoData) {
        if (otoData.stopId && otoData.stopPrice) {
          const currentStopLeg = order?.oto?.find(
            (leg) => leg.id === otoData.stopId,
          );
          if (currentStopLeg && currentStopLeg.price !== otoData.stopPrice) {
            otoUpdates.push({
              otoId: otoData.stopId,
              newPrice: otoData.stopPrice,
            });
          }
        }
        if (otoData.profitId && otoData.profitPrice) {
          const currentProfitLeg = order?.oto?.find(
            (leg) => leg.id === otoData.profitId,
          );
          if (
            currentProfitLeg &&
            currentProfitLeg.price !== otoData.profitPrice
          ) {
            otoUpdates.push({
              otoId: otoData.profitId,
              newPrice: otoData.profitPrice,
            });
          }
        }
      }

      // Apply updates if we have any changes (quantity, price, or OTO)
      if (quantityChanged || priceChanged || otoUpdates.length > 0) {
        if (otoUpdates.length > 0) {
          updates.otoUpdates = otoUpdates;
        }
        updateOrderComplete(orderId, updates);
      }

      closeQuantityModal();
    },
    [quantityModalData, updateOrderComplete, closeQuantityModal],
  );

  // Don't render anything if modal is not visible
  if (!quantityModalVisible) {
    return null;
  }

  return (
    <QuantityEditModal
      visible={quantityModalVisible}
      onHide={closeQuantityModal}
      onConfirm={handleQuantityModalConfirm}
      initialQuantity={quantityModalData.currentQuantity}
      initialPrice={quantityModalData.price}
      orderType={quantityModalData.orderType}
      symbol={quantityModalData.symbol}
      initialOrder={quantityModalData.order}
    />
  );
};

export default QuantityModalProvider;
